import { useSelector } from "react-redux";
import { MODULE_KEY } from "./actions";
import { ACTION } from "src/util/utils";
import Button from "src/components/common/Button/Button";
import { DeleteOutlined, EditOutlined } from "@mui/icons-material";
import useRolePermission from "src/hooks/useRolePermission";
import { formatDate } from "src/util/date";
import { OfficeCollectionFilterOptions } from "./types";

export default function filterColumns(
  editDrawer?: ((record: any) => void) | any,
  deleteModal?: ((record: any) => void) | any,
  fullFilterOptions?: Partial<OfficeCollectionFilterOptions>
) {
  const { data, loading } = useSelector(
    (state: any) => state[`${MODULE_KEY}${ACTION.STATE_KEY}`]
  );
  const vendorMap = new Map();
  const collectionMap = new Map();
  const collectionTypeMap = new Map();
  const { hasPermission } = useRolePermission();

  data?.forEach((row: any) => {
    const vendor = row.Vendor?.trim() ?? "";
    const collection = row.Collection?.trim() ?? "";
    const collectionType = row.CollectionType?.trim() ?? "";

    if (vendor) {
      vendorMap.set(vendor, { text: vendor, value: vendor });
    }
    if (collection) {
      collectionMap.set(collection, { text: collection, value: collection });
    }
    if (collectionType) {
      collectionTypeMap.set(collectionType, {
        text: collectionType,
        value: collectionType,
      });
    }
  });

  const vendorNameFromCurrentPage = Array.from(vendorMap.values()).sort((a: any, b: any) =>
    a.text.toLowerCase().localeCompare(b.text.toLowerCase())
  );

  const collectionNameFromCurrentPage = Array.from(collectionMap.values()).sort((a: any, b: any) =>
    a.text.toLowerCase().localeCompare(b.text.toLowerCase())
  );

  const collectionTypeNameFromCurrentPage = Array.from(collectionTypeMap.values()).sort((a: any, b: any) =>
    a.text.toLowerCase().localeCompare(b.text.toLowerCase())
  );

  const vendorName =
    fullFilterOptions?.vendorName && fullFilterOptions.vendorName.length > 0
      ? fullFilterOptions.vendorName
      : vendorNameFromCurrentPage;

  const collectionName =
    fullFilterOptions?.collectionName && fullFilterOptions.collectionName.length > 0
      ? fullFilterOptions.collectionName
      : collectionNameFromCurrentPage;

  const collectionTypeName =
    fullFilterOptions?.collectionTypeName && fullFilterOptions.collectionTypeName.length > 0
      ? fullFilterOptions.collectionTypeName
      : collectionTypeNameFromCurrentPage;

  const columns = [
    {
      id: 'OfficeNum',
      header: 'OFFICE NUMBER',
      accessorKey: 'OfficeNum',
      size: 150,
      enableColumnFilter: true,
      filterFn: 'contains',
      enableSorting: true,
    },
    {
      id: 'LocationName',
      header: 'OFFICE NAME',
      accessorKey: 'LocationName',
      size: 150,
      enableColumnFilter: true,
      filterFn: 'contains',
      enableSorting: true,
    },
    {
      id: 'FrameArrangerID',
      header: 'ASSORTMENT ID',
      accessorKey: 'FrameArrangerID',
      size: 150,
      enableColumnFilter: true,
      filterFn: 'contains',
      enableSorting: true,
    },
    {
      id: 'AssortmentName',
      header: 'ASSORTMENT NAME',
      accessorKey: 'AssortmentName',
      size: 160,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'contains',
    },
    {
      id: 'ArrangerQty',
      header: 'ASSORTMENT QUANTITY',
      accessorKey: 'ArrangerQty',
      size: 160,
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      id: 'Vendor',
      header: 'VENDOR NAME',
      accessorKey: 'Vendor',
      size: 150,
      enableColumnFilter: true,
      enableSorting: true,
      filterVariant: 'multi-select',
      filterSelectOptions: vendorName 
    },
    {
      id: 'Collection',
      header: 'COLLECTION NAME',
      accessorKey: 'Collection',
      size: 160,
      enableColumnFilter: true,
      enableSorting: true,
      filterVariant: 'multi-select',
      filterSelectOptions: collectionName
    },
    {
      id: 'CollectionType',
      header: 'COLLECTION TYPE',
      accessorKey: 'CollectionType',
      size: 160,
      enableColumnFilter: true,
      enableSorting: true,
      filterVariant: 'multi-select',
      filterSelectOptions: collectionTypeName 
    },
    {
      id: 'Region',
      header: 'REGION',
      accessorKey: 'Region',
      size: 100,
      enableColumnFilter: true,
      filterFn: 'contains',
      enableSorting: false,
    },
    {
      id: 'District',
      header: 'DISTRICT',
      accessorKey: 'District',
      size: 100,
      enableColumnFilter: true,
      filterFn: 'contains',
      enableSorting: false,
    },
    {
      id: 'UpdatedDate',
      header: 'START DATE',
      accessorKey: 'UpdatedDate',
      size: 120,
      enableColumnFilter: false,
      enableSorting: true,
      Cell: ({ cell }: { cell: any  }) => {
        const startDate = cell.row?.original.UpdatedDate;
        if (!startDate || startDate === "null") {
            return "N/A";
        }
        const formattedStartDate = formatDate(startDate);
        return formattedStartDate;
       }
    },
    {
      id: 'UpdateNotes',
      header: 'CHANGED NOTES',
      accessorKey: 'UpdateNotes',
      size: 130,
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      id: 'StopDate',
      header: 'STOP DATE',
      accessorKey: 'StopDate',
      size: 120,
      enableColumnFilter: false,
      enableSorting: true,
      Cell: ({ cell }: { cell: any  }) => {
        const stopDate = cell.row?.original.StopDate;
        if (!stopDate || stopDate === "null") {
            return "N/A";
        }
        const formattedStopDate = formatDate(stopDate);
        return formattedStopDate;   

       }
    },
    {
      accessorKey: 'actions',
      header: 'ACTIONS',
      size: 80,
      enableColumnFilter: false,
      enableSorting: false,
      Cell: ({ cell }: { cell: any }) => {
        return (
          <>
            <div className="d-flex w-100">
              <Button
                icon={<EditOutlined sx={{ fontSize: 20 ,marginLeft: "10px", marginRight: "10px"}}/>}
                children={undefined}
                onClick={() => editDrawer(cell.row?.original)}
                disabled={!hasPermission("officecollectionsmapping_edit")}
              ></Button>
              <Button
                style={{ marginLeft: "10px", marginRight: "10px" }}
                icon={<DeleteOutlined sx={{ fontSize: 20,marginLeft: "10px", marginRight: "10px" }} />}
                onClick={() => deleteModal(cell.row?.original?.ID)}
                children={undefined}
                danger={true}
                disabled={!hasPermission("officecollectionsmapping_delete")}
              ></Button>
            </div>
          </>
        );
      }
    }
  ]

  return { columns, data, loading };
}
